import { useCallback, useEffect, useState } from 'react';
import { Apple, Loader2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import api from '@/lib/api';

/**
 * AppleMusicSyncCard — compact Profile widget showing how much of the
 * user's collection is already linked to a direct Apple Music track
 * page, plus a one-tap "Sync now" that runs the cache-only bulk
 * backfill sweep (no Songstats API budget spent).
 *
 * The card is intentionally lean: one stat sentence, one button, one
 * progress bar. It disappears completely when the user has zero cards
 * so we don't clutter the Profile page for brand-new accounts.
 */
export default function AppleMusicSyncCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/cards/apple-music/stats');
      setStats(data);
    } catch (_e) {
      /* soft fail — widget just won't render */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/cards/apple-music/backfill-cached');
      const updated = data?.updated ?? 0;
      if (updated > 0) {
        toast.success(
          `Linked ${updated} ${updated === 1 ? 'track' : 'tracks'} to Apple Music`
        );
      } else {
        toast.info('Nothing new to link right now.');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apple Music sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading || !stats || stats.total === 0) return null;

  const total = stats.total || 0;
  const linked = stats.with_direct_link || 0;
  const candidates = stats.candidates || 0;
  const pct = total > 0 ? Math.round((linked / total) * 100) : 0;
  const canSync = candidates > 0;

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 sm:p-5"
      data-testid="apple-music-sync-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Apple className="w-4 h-4 text-[hsl(var(--primary))]" />
        <h3 className="font-display font-semibold">Apple Music sync</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        <span
          className="font-semibold text-foreground tabular-nums"
          data-testid="apple-music-sync-linked-count"
        >
          {linked}
        </span>{' '}
        of {total} {total === 1 ? 'track' : 'tracks'} have direct Apple Music links.
        {canSync ? (
          <>
            {' '}
            <span className="text-foreground/80" data-testid="apple-music-sync-candidate-count">
              {candidates} {candidates === 1 ? 'track' : 'tracks'} can be linked using cached data.
            </span>
          </>
        ) : (
          <> You&rsquo;re all synced up.</>
        )}
      </p>
      <div className="mb-3">
        <Progress
          value={pct}
          className="h-1.5"
          data-testid="apple-music-sync-progress"
          aria-label={`Apple Music link coverage: ${pct}%`}
        />
        <div className="mt-1 flex justify-between text-[10px] font-mono text-muted-foreground">
          <span>{pct}% linked</span>
          <span>{total - linked} remaining</span>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleSync}
        disabled={syncing || !canSync}
        className="rounded-lg h-8 text-xs"
        data-testid="apple-music-sync-button"
        title={
          canSync
            ? 'Backfill Apple Music links using cached data (no extra Songstats calls)'
            : 'Nothing to sync right now'
        }
      >
        {syncing ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
        )}
        {syncing ? 'Syncing\u2026' : canSync ? 'Sync now' : 'Nothing to sync'}
      </Button>
    </div>
  );
}
